// === INBOEDEL — coverage constants ===
// EC is an array of at most ONE entry (schema maxItems 1): 5450 (Mobiele elektronica Allrisk)
// or 5470 (Smart Home). Smart Home REPLACES Mobiele elektronica on the wire; the screen keeps
// listing electronics as included and prices the Smart Home row from a second pricing call
// while the toggle is off.
export const EC_CODE_MOBIEL = '5450';
export const EC_CODE_SMARTHOME = '5470';

/** Default insured sum per EC row (the schema's 25.000 default resp. the 3.000 Smart Home choice). */
export const EC_DEFAULT_SUM: Readonly<Record<string, number>> = {
  [EC_CODE_MOBIEL]: 25000,
  [EC_CODE_SMARTHOME]: 3000,
};

/**
 * The ONE EC row the product carries for a coverage selection: Smart Home when selected, else
 * Mobiele elektronica (always part of the base), none only when electronics is explicitly off.
 */
export const selectedEcCode = (
  dekkingen: { Smarthome?: string; Mobieleelektronica?: string } | null | undefined,
): string | null => {
  if (dekkingen?.Smarthome === 'J') return EC_CODE_SMARTHOME;
  return dekkingen?.Mobieleelektronica === 'N' ? null : EC_CODE_MOBIEL;
};

/**
 * Reduce a document's EC array to the selected row (response cleaners, quote and application
 * documents). A document without that row is left alone: the request builders create rows,
 * cleaners only drop the other one.
 */
export const keepSelectedEcRow = (
  pp: { EC?: EcEntry | EcEntry[] } | null | undefined,
  dekkingen: { Smarthome?: string; Mobieleelektronica?: string } | null | undefined,
): void => {
  if (!pp?.EC) return;
  const code = selectedEcCode(dekkingen);
  const rows = (Array.isArray(pp.EC) ? pp.EC : [pp.EC]).filter((e) => e.EC_CODE === code);
  if (rows.length > 0) pp.EC = rows;
};

/** One entry of the EC coverage array. Only the fields read by name are typed;
 *  everything else the BFF puts on the entry passes through untyped. */
export interface EcEntry {
  EC_CODE: string;
  EC_VERZSOM?: number | string;
  EC_BTP?: number | string;
  EC_TTOT?: number | string;
  [field: string]: unknown;
}

/** Find an EC entry by code from the EC array, returns undefined if not found */
export const getEc = (
  pp: { EC?: EcEntry | EcEntry[] } | null | undefined,
  code: string,
): EcEntry | undefined => {
  if (!pp?.EC) return undefined;
  const arr = Array.isArray(pp.EC) ? pp.EC : [pp.EC];
  return arr.find((e) => e.EC_CODE === code);
};

export type InboedelCoverageKey =
  | 'glass'
  | 'jewelry'
  | 'smarthome'
  | 'ownerInterest'
  | 'tenantInterest'
  | 'specialPossessions';

// The six optional-coverage toggle cards, previously six near-identical JSX clones in
// InboedelScreen. `key` doubles as the activeInfo id and the coverageInfo i18n segment
// (screens.inboedel.coverageInfo.<key>.title / .item<n>).
//
// Every coverage reads its premium from exactly one source: a PP section (ppSection)
// XOR an EC-array entry (ecCode) — encoded as a union so a descriptor with both, or
// neither, fails to compile.
type InboedelCoverageBase = {
  key: InboedelCoverageKey;
  dekkingenKey: string;      // Supportsection.Dekkingen.<dekkingenKey>
  labelKey: string;          // screens.inboedel.fields.*
  infoItemCount: number;
  infoHtmlItems?: number[];  // item numbers rendered via dangerouslySetInnerHTML
  hiddenWhenGlassCovered?: boolean;  // Glas: hidden when another selected product already covers glass (config.glassPremium.coveredByProducts)
  errorFieldPath?: string;   // validation error rendered on the card itself (no sub-input of its own)
  errorAnchorId?: string;    // element id the *_ERROR_FIELD_IDS map scrolls to for that error
} & (
  | { ppSection: 'GP' | 'LS' | 'OP' | 'HU' | 'KB'; ecCode?: never }
  | { ppSection?: never; ecCode: string }  // EC-array coverage (Smart Home) — premium read from the EC entry
);

// The BFF-priced amount coverages (LS/OP/HU/KB) share one band: values above the max show the
// descriptor's error, values inside [recalcMin, max] re-trigger the BFF calc. Glas has no band —
// its herbouwwaarde input is what the Midglas engine rates over (see utils/glassPremium.ts).
export const COVERAGE_AMOUNT_MAX = 50000;
export const COVERAGE_AMOUNT_RECALC_MIN = 15001;

export type InboedelCoverageDescriptor =
  | (InboedelCoverageBase & {
      kind: 'amount';
      amountInputId: string;
      amountFieldPath: string;
      amountLabelKey?: string;        // defaults to screens.inboedel.fields.coverageAmount
      amountPlaceholderKey?: string;  // defaults to screens.inboedel.aboveAmountPlaceholder
      lockFieldKey?: string;          // infofolioFilledFields entry that makes the input read-only
    } & (
      | { amountMax: number; maxErrorKey: string; recalcMin: number }
      | { amountMax?: never; maxErrorKey?: never; recalcMin?: never }
    ))
  | (InboedelCoverageBase & {
      kind: 'plain' | 'sumSelect';
      amountInputId?: never;
      amountFieldPath?: never;
      amountLabelKey?: never;
      amountPlaceholderKey?: never;
      lockFieldKey?: never;
      amountMax?: never;
      maxErrorKey?: never;
      recalcMin?: never;
    });

export const INBOEDEL_OPTIONAL_COVERAGES: InboedelCoverageDescriptor[] = [
  {
    key: 'glass',
    dekkingenKey: 'Glas',
    labelKey: 'screens.inboedel.fields.glassCoverage',
    ppSection: 'GP',
    infoItemCount: 3,
    // No herbouwwaarde input: the tile only exists when Infofolio supplied the value
    // (inboedelGlassAllowed). Its one error (value lost) renders on this card.
    kind: 'plain',
    hiddenWhenGlassCovered: true,
    errorFieldPath: 'Contractdocument.PP.BA.BA_HERBWRD',
    errorAnchorId: 'glass-rebuild-value',
  },
  {
    key: 'jewelry',
    dekkingenKey: 'Lijfsieraden',
    labelKey: 'screens.inboedel.fields.jewelryCoverage',
    ppSection: 'LS',
    infoItemCount: 4,
    infoHtmlItems: [4],
    kind: 'amount',
    amountInputId: 'jewelry-amount',
    amountFieldPath: 'Contractdocument.PP.LS.LS_VERZSOM',
    maxErrorKey: 'screens.inboedel.validation.jewelryMaxAmount',
    amountMax: COVERAGE_AMOUNT_MAX,
    recalcMin: COVERAGE_AMOUNT_RECALC_MIN,
  },
  {
    key: 'smarthome',
    dekkingenKey: 'Smarthome',
    labelKey: 'screens.inboedel.fields.smarthomeCoverage',
    ecCode: EC_CODE_SMARTHOME,
    infoItemCount: 3,
    kind: 'sumSelect',
  },
  {
    key: 'ownerInterest',
    dekkingenKey: 'Eigenaarsbelang',
    labelKey: 'screens.inboedel.fields.ownerInterestCoverage',
    ppSection: 'OP',
    infoItemCount: 3,
    kind: 'amount',
    amountInputId: 'owner-interest-amount',
    amountFieldPath: 'Contractdocument.PP.OP.OP_VERZSOM',
    maxErrorKey: 'screens.inboedel.validation.genericMaxAmount',
    amountMax: COVERAGE_AMOUNT_MAX,
    recalcMin: COVERAGE_AMOUNT_RECALC_MIN,
  },
  {
    key: 'tenantInterest',
    dekkingenKey: 'Huurdersbelang',
    labelKey: 'screens.inboedel.fields.tenantInterestCoverage',
    ppSection: 'HU',
    infoItemCount: 3,
    kind: 'amount',
    amountInputId: 'tenant-interest-amount',
    amountFieldPath: 'Contractdocument.PP.HU.HU_VERZSOM',
    maxErrorKey: 'screens.inboedel.validation.genericMaxAmount',
    amountMax: COVERAGE_AMOUNT_MAX,
    recalcMin: COVERAGE_AMOUNT_RECALC_MIN,
  },
  {
    key: 'specialPossessions',
    dekkingenKey: 'Bijzonderebezittingen',
    labelKey: 'screens.inboedel.fields.specialPossessionsCoverage',
    ppSection: 'KB',
    infoItemCount: 4,
    infoHtmlItems: [4],
    kind: 'amount',
    amountInputId: 'special-possessions-amount',
    amountFieldPath: 'Contractdocument.PP.KB.KB_VERZSOM',
    maxErrorKey: 'screens.inboedel.validation.specialMaxAmount',
    amountMax: COVERAGE_AMOUNT_MAX,
    recalcMin: COVERAGE_AMOUNT_RECALC_MIN,
  },
];
